/**Import react section */
import React, { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import propTypes from 'prop-types'
/**Import components section */
import TextBox from '../../common/TextBox'
import TabsWrapper from '../../common/TabsWrapper'
import PanelHeader from '../../common/lateralPanel/PanelHeaderIcon'
import TimeLineCarrier from './TimeLineCarrier'
import PackageTracking from './PackageTracking'
import ButtonSaved from '../../common/Buttons/ButtonSaved'
import Select from '../../common/Selector'

/**Import MaterialUi section */
import { Grid, Typography, Drawer } from '@material-ui/core'
import MomentUtils from '@date-io/moment'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import moment from 'moment'
/**Import resources section */
import { SPARE_PARTS_IMBERA_USA, SPARE_PARTS_IMBERA_MX, PORTAL_ADMINISTRATOR } from '../../../resources/RolesEnum'
import { SpareParRequestStatus, WarehouseType } from '../../../store/helpers/AppConstants'
import { hasAuthorization } from '../../../store/session/sessionActions'
import { panelEditUseStyles } from '../resources/useStyles'
import { GetStatusTracking } from '../../../store/helpers/SelectOptions'
import { isCancelledStatus, isDeliveredStatus } from '../../../store/helpers/sparePartsStatusColors'
import { formatDeliveryDate, validateDeliveryDate } from '../../../store/helpers/deliveryDate'
import iconFailureDoor from '../../../resources/images/failureWarranty/doorWhite.svg'

/**
 * Panel to edit information traking
 * @param {} props
 */
const EditPanel = (props) => {
	const classes = panelEditUseStyles()
	let match = useRouteMatch()
	const token = match.params.token
	const statusOptions = GetStatusTracking()
	const { onUpdateSpartPart, isSaving, onCloseButtonClick, userProfile, status, onCheckShipping, isGettingTrackingData, requestCreationDate } = props
	const [partModel, setPartModel] = useState(null)
	const [carrierData, setCarrierData] = useState(null)
	const [deliveryDateError, setDeliveryDateError] = useState(null)
	const [isClosedLineNoticeShown, setIsClosedLineNoticeShown] = useState(false)
	const [isSavingDelivery, setIsSavingDelivery] = useState(false)
	const [savedDeliveryDate, setSavedDeliveryDate] = useState(null)
	let isAuthorizerCanEditRoleImberaMx = hasAuthorization(userProfile, [SPARE_PARTS_IMBERA_MX]) && status === SpareParRequestStatus.SEND_TO_MX
	let isAuthorizerCanEditRoleImberaUsa =
		hasAuthorization(userProfile, [SPARE_PARTS_IMBERA_USA]) &&
		(status === SpareParRequestStatus.IN_PROCESS || status === SpareParRequestStatus.SEND_TO_MX)
	let isAdmin = hasAuthorization(userProfile, [PORTAL_ADMINISTRATOR])

	let canBeEdit =
		(isAdmin && !!partModel) ||
		(isAuthorizerCanEditRoleImberaMx && partModel && partModel.Warehouse === WarehouseType.MEXICO) ||
		(isAuthorizerCanEditRoleImberaUsa && partModel && partModel.Warehouse === WarehouseType.USA)

	/**
	 * Re-seeds the panel whenever the opened line or its carrier data change -- a successful
	 * Search replaces both. A Delivered the user picked but has not saved yet survives the
	 * re-seed (IS2-240): it used to save on change, so there was nothing pending to lose. It is
	 * dropped only when the line itself arrives delivered, which is the carrier's own answer.
	 */
	useEffect(() => {
		setPartModel((current) => {
			const incoming = props.part
			const keepsPendingDelivery =
				!!current &&
				!!incoming &&
				current.Token === incoming.Token &&
				isDeliveredStatus(current.Status) &&
				!isDeliveredStatus(incoming.Status)
			return keepsPendingDelivery ? { ...incoming, Status: current.Status, ReleaseDate: current.ReleaseDate } : incoming
		})
		setCarrierData(props.carrierData)
	}, [props.part, props.carrierData])

	/**
	 * Whether the line is already closed, so it cannot take a delivery date (IS2-240): editing the
	 * date of a closed line is out of scope and the backend refuses it. The backend closes a line
	 * on Delivered and on Cancelled alike (SparePartSales.IsDelivery), so both count. Two signals,
	 * because neither covers every case:
	 *  - the live status, not the copy the panel was opened with: the panel stays open after a
	 *    save, and the copy still holds the status from before it;
	 *  - the line's IsClosed, for a line whose status no longer says it is closed. The backend
	 *    may not send IsClosed; without it only the status decides.
	 */
	const liveStatus =
		props.trackingStatus !== undefined && props.trackingStatus !== null ? props.trackingStatus : props.part && props.part.Status
	const isLineClosed = isDeliveredStatus(liveStatus) || isCancelledStatus(liveStatus) || !!(props.part && props.part.IsClosed === true)

	/** A line the user just marked Delivered, still waiting for its delivery date (IS2-240). */
	const isDeliveryPending = !!partModel && isDeliveredStatus(partModel.Status) && !isLineClosed

	/**
	 * The panel's save. A pending delivery goes out as the status change it is -- tagged 'Status',
	 * with the delivery date the user confirmed -- once that date passes validation. Anything else
	 * is a comment save, tagged COMMENTS, never STATUS: the backend applies the line's status by
	 * hand only on a save tagged STATUS (in any case), and this one carries the status the panel
	 * was opened with.
	 */
	const updateSpartPart = () => {
		if (!onUpdateSpartPart || isSavingDelivery) return
		if (isDeliveryPending) {
			const error = validateDeliveryDate(partModel.ReleaseDate, requestCreationDate)
			setDeliveryDateError(error)
			if (error) return
			// The save can take seconds and nothing else on the panel changes until it is back, so
			// it is held here: without it the user clicks again and sends the delivery twice, and the
			// second one is refused because the first already closed the line.
			const deliveredOn = moment(partModel.ReleaseDate).format('MM/DD/YYYY')
			const saving = onUpdateSpartPart({ ...partModel, ReleaseDate: formatDeliveryDate(partModel.ReleaseDate) }, 'Status')
			if (saving && typeof saving.finally === 'function') {
				setIsSavingDelivery(true)
				saving.finally(() => {
					setIsSavingDelivery(false)
					// Shown only once the folio refresh confirms the line closed (see the render), so a
					// refused save never reads as saved.
					setSavedDeliveryDate(deliveredOn)
				})
			}
			return
		}
		onUpdateSpartPart(partModel, 'COMMENTS')
	}

	/**
	 * Function ot update status traking. Tagged 'Status', not 'STATUS', on purpose: Open.js
	 * refreshes the view only on that exact value, and the backend reads it as an explicit status
	 * change without regard to case. The warranty twin sends 'STATUS' because its Open.js has no
	 * such check -- do not align the two without changing Open.js too.
	 *
	 * Delivered is the exception (IS2-240): it waits for the delivery date, which starts as today,
	 * and is sent by the panel's save. On a line already closed it is not sent at all -- the
	 * backend would refuse it -- and the panel says why. Any other status drops that date and
	 * saves right away.
	 * @param {*} name
	 * @param {*} value
	 */
	const updateStatusTraking = (name, value) => {
		setDeliveryDateError(null)
		setIsClosedLineNoticeShown(false)
		setSavedDeliveryDate(null)
		if (isDeliveredStatus(value)) {
			if (isLineClosed) {
				setIsClosedLineNoticeShown(true)
				return
			}
			setPartModel({ ...partModel, [name]: value, ReleaseDate: moment().startOf('day') })
			return
		}
		const updated = { ...partModel, [name]: value }
		delete updated.ReleaseDate
		setPartModel(updated)
		if (onUpdateSpartPart) {
			onUpdateSpartPart(updated, 'Status')
		}
	}

	const onDeliveryDateChange = (date) => {
		setDeliveryDateError(null)
		setPartModel({ ...partModel, ReleaseDate: date })
	}

	/**On change properties */
	const onPropertyChange = (event) => {
		let { name, value } = event.target
		setPartModel({
			...partModel,
			[name]: value
		})
	}

	/**
	 * The tab has two reasons to hold something, and it used to know only one.
	 *
	 * The timeline is the ordinary case. The other appears once a shipment is delivered: the
	 * backend deregisters it from Trackingmore, so its lookup stops answering and there is no
	 * timeline to draw -- and that is the normal end of the cycle, not a failure. The tab is where
	 * the user goes looking for the details, so it is where they have to be told there are none.
	 *
	 * Keeping this as a named expression rather than inline in the config is deliberate: the two
	 * cases are worth reading as two, and the condition is about to be referenced twice.
	 */
	const hasTimeline = !!(carrierData && carrierData.origin_info && carrierData.origin_info.trackinfo)
	// trackingStatus is the line's status as the order currently holds it; partModel carries the
	// copy the panel was opened with, which goes stale the moment a delivery event arrives while
	// the panel is already open. Falls back to the copy so the panel still works if no live
	// status reaches it.
	const hasLiveTrackingStatus = props.trackingStatus !== undefined && props.trackingStatus !== null
	const currentTrackingStatus = hasLiveTrackingStatus ? props.trackingStatus : partModel && partModel.Status
	const isDeliveredWithoutDetails = !hasTimeline && isDeliveredStatus(currentTrackingStatus)

	/**
	 * The option the status selector should be showing.
	 *
	 * Matched without case because the two sides spell the status differently: the options are
	 * written Transit and Delivered, and what the carrier stores is transit and delivered. An
	 * exact comparison found nothing, so the selector sat empty next to a row whose chip read
	 * Transit -- the same value, failing to recognise itself.
	 *
	 * Read from the editable model rather than from the live status, which is a distinction worth
	 * stating because the first attempt got it wrong. This field saves on change, and the store
	 * only learns the new value once the PUT comes back; sourcing the display from the store meant
	 * a hand-picked status did not appear until the round trip finished, and reverted silently if
	 * the save failed. The live status answers a different question -- whether the shipment was
	 * delivered -- and stays out of this one.
	 */
	const selectedStatusOption =
		statusOptions.find((option) => option.label.toLowerCase() === String((partModel && partModel.Status) || '').trim().toLowerCase()) || null

	const tabsConfig = [
		{
			code: 'comments',
			label: 'COMMENTS',
			content: ''
		},
		{
			code: 'TrackingNumber',
			label: 'DETAIL VIEW',
			content: '',
			disabled: !hasTimeline && !isDeliveredWithoutDetails
		}
	]
	/**Consult tracking number */
	const consultDataByTrackingnNumber = () => {
		if (onCheckShipping) {
			onCheckShipping(partModel.Carrier, partModel.NumberTracking, token)
		}
	}

	const onRenderTabContent = (tab) => {
		let tabContent = null
		switch (tab.code) {
			case 'comments': {
				tabContent = (
					<Grid item xs={12} className={classes.editTab}>
						<TextBox
							active={true}
							disabled={isSaving || !canBeEdit}
							fullWidth
							autoFocus
							multiline
							rows={4}
							name='Comments'
							variant='outlined'
							margin='dense'
							label='COMMENTS'
							onChange={onPropertyChange}
							value={partModel ? partModel.Comments : ''}
						/>
					</Grid>
				)
				break
			}
			case 'TrackingNumber': {
				if (hasTimeline) {
					tabContent = (
						<>
							<TimeLineCarrier shippingHistory={carrierData.origin_info.trackinfo} />
						</>
					)
				} else if (isDeliveredWithoutDetails) {
					tabContent = (
						<Grid item xs={12} className={classes.editTab}>
							<Typography variant='body2'>Shipment delivered — no tracking details available.</Typography>
						</Grid>
					)
				} else {
					tabContent = null
				}
				break
			}
			default: {
				tabContent = null
				break
			}
		}
		return tabContent
	}

	return (
		<LateralPanel onClose={onCloseButtonClick}>
			<PanelHeader
				title={`TRACKING DETAILS`}
				onSaveButtonClick={updateSpartPart}
				onCloseButtonClick={onCloseButtonClick}
				canBeSaved={canBeEdit}
				isSaving={!!isSaving || isSavingDelivery}
				icon={iconFailureDoor}
				isIconSvgOrPng={true}
			/>
			<Grid container>
				<Grid item xs={12} className={classes.titleContainer}>
					<div className={classes.titleLeftContent}>
						<div className={classes.flex}>
							<Typography variant='subtitle1' className={classes.titleBold}>{`Status: `}</Typography>
							{partModel && (
								<div style={{ width: '150px' }}>
									<Select
										options={statusOptions}
										isDisabled={!canBeEdit}
										value={selectedStatusOption}
										onChange={(option) => updateStatusTraking('Status', option.label)}
										margin='dense'
									/>
								</div>
							)}
						</div>
						{savedDeliveryDate && isLineClosed && !isClosedLineNoticeShown && (
							<Typography variant='caption' style={{ color: '#1E7A45' }}>
								Saved. The line was delivered on {savedDeliveryDate}.
							</Typography>
						)}
						{isClosedLineNoticeShown && (
							<Typography variant='caption' color='textSecondary'>
								This line is already closed. Its delivery date cannot be changed.
							</Typography>
						)}
						{isDeliveryPending && (
							<div style={{ width: '220px' }}>
								<MuiPickersUtilsProvider utils={MomentUtils} locale='en'>
									<KeyboardDatePicker
										id='tracking-delivery-date'
										required
										autoOk
										disableToolbar
										variant='inline'
										inputVariant='outlined'
										margin='dense'
										format='MM/DD/YYYY'
										label='Delivery date'
										value={partModel.ReleaseDate || null}
										disabled={!canBeEdit}
										disableFuture
										maxDateMessage='The delivery date cannot be in the future.'
										minDate={requestCreationDate || undefined}
										minDateMessage="The delivery date cannot be earlier than the request's creation date."
										invalidDateMessage='Enter a valid delivery date.'
										onChange={onDeliveryDateChange}
										error={!!deliveryDateError}
										helperText={deliveryDateError || (isSavingDelivery ? 'Saving…' : 'Save to mark the line as delivered.')}
										KeyboardButtonProps={{ 'aria-label': 'Change delivery date' }}
									/>
								</MuiPickersUtilsProvider>
							</div>
						)}
					</div>
					{partModel && partModel.NumberTracking && (
						<Grid item xs={12} className={classes.titleRightContent}>
							<Grid container spacing={1} style={{ display: 'flex', alignSelf: 'center', alignItems: 'center' }}>
								<Grid item xs={9}>
									<TextBox
										active={true}
										disabled={true}
										fullWidth
										autoFocus
										name='NumberTracking'
										variant='outlined'
										margin='dense'
										label='Tracking number'
										onChange={onPropertyChange}
										value={partModel ? partModel.NumberTracking : ''}
									/>
								</Grid>
								{!partModel.IsOtherCarrier && partModel.Carrier && (
									<Grid item xs={3}>
										<ButtonSaved title={'Search'} onSavedClick={consultDataByTrackingnNumber} isSaving={isGettingTrackingData} />
									</Grid>
								)}
							</Grid>
							<br />
						</Grid>
					)}
				</Grid>
				<Grid item xs={12}>
					{carrierData && <PackageTracking carrierData={carrierData} />}
				</Grid>
				<Grid item xs={12}>
					<TabsWrapper tabs={tabsConfig} onRenderTabContent={onRenderTabContent} className={classes.tabs} />
				</Grid>
			</Grid>
		</LateralPanel>
	)
}

const LateralPanel = (props) => {
	const { onClose, children } = props
	const classes = panelEditUseStyles()

	return (
		<Drawer anchor='right' open={props.open} onClose={onClose}>
			<div className={classes.drawerContent} tabIndex={0}>
				{children}
			</div>
		</Drawer>
	)
}
LateralPanel.propTypes = {
	open: propTypes.bool.isRequired
}
LateralPanel.defaultProps = {
	open: true
}

export default EditPanel
